import React, { useState, useEffect, useMemo, useCallback } from 'react';
import TabPageLayout, { DataCard, EmptyState, PageLoader } from './TabPageLayout';
import { MetricTile } from './MetricTile';
import { ExportMenu } from './ExportMenu';
import { DateRangeControl } from './DateRangeControl';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import { orgApi } from '../utils/apiV2';
import { toast } from 'sonner';
import {
  FileBarChart, DollarSign, AlertCircle, RotateCcw, Gift,
  ArrowUpDown, CalendarRange, SlidersHorizontal,
} from 'lucide-react';

// ── date helpers ──────────────────────────────────────────────────────────
const iso = (d) => d.toISOString().slice(0, 10);
const presetRange = (key) => {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  switch (key) {
    case 'thisWeek': { const s = new Date(now); s.setDate(now.getDate() - now.getDay()); return { startDate: iso(s), endDate: iso(now) }; }
    case 'thisMonth': return { startDate: iso(new Date(y, m, 1)), endDate: iso(now) };
    case 'lastMonth': return { startDate: iso(new Date(y, m - 1, 1)), endDate: iso(new Date(y, m, 0)) };
    case 'thisYear': return { startDate: `${y}-01-01`, endDate: iso(now) };
    case 'last90': { const s = new Date(now); s.setDate(now.getDate() - 90); return { startDate: iso(s), endDate: iso(now) }; }
    default: return { startDate: `${y}-01-01`, endDate: iso(now) };
  }
};
const fmtDate = (s) => (s ? new Date(s + (s.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const PRESETS = [
  { id: 'thisWeek', label: 'This week' },
  { id: 'thisMonth', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
  { id: 'last90', label: 'Last 90 days' },
  { id: 'thisYear', label: 'This year' },
  { id: 'custom', label: 'Custom range' },
];
const BASIS = [
  { id: 'trip', label: 'By trip date' },
  { id: 'payment', label: 'By payment date' },
  { id: 'created', label: 'By booking date' },
];
const STATUS_CHIPS = [
  { id: 'all', label: 'All trips' },
  { id: 'owed', label: 'Owed' },
  { id: 'collected', label: 'Collected' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'cancelled', label: 'Cancelled / Refunded' },
];

// Restrained badge palette: paid = BookingFlows blue, owed/cancelled = red, deposit/pending = gray
const PaymentBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  let cls = 'bg-gray-100 text-gray-600';
  if (s === 'paid' || s === 'completed') cls = 'bg-blue-50 text-blue-700';
  else if (s === 'refunded') cls = 'bg-red-50 text-red-600';
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}>
      {(status || '—').replace('_', ' ')}
    </span>
  );
};

const selectCls = 'h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer';

const ReportsTab = () => {
  const { formatMoney } = useOperatorLocale();
  const [dateBasis, setDateBasis] = useState('trip');
  const [preset, setPreset] = useState('thisMonth');
  const [range, setRange] = useState(() => presetRange('thisMonth'));
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceId, setServiceId] = useState('all');
  const [assetId, setAssetId] = useState('all');
  const [services, setServices] = useState([]);
  const [assets, setAssets] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: 'tripDate', dir: 'asc' });

  useEffect(() => {
    orgApi.getServices().then((r) => setServices(r.data?.services || (Array.isArray(r.data) ? r.data : []))).catch(() => {});
    orgApi.getAssets().then((r) => setAssets(r.data?.assets || (Array.isArray(r.data) ? r.data : []))).catch(() => {});
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await orgApi.getReportTrips({
        dateBasis, startDate: range.startDate, endDate: range.endDate,
        serviceId: serviceId !== 'all' ? serviceId : undefined,
        assetId: assetId !== 'all' ? assetId : undefined,
      });
      setData(res);
    } catch (e) { toast.error('Could not load report'); setData(null); }
    finally { setLoading(false); }
  }, [dateBasis, range.startDate, range.endDate, serviceId, assetId]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const onPreset = (key) => { setPreset(key); if (key !== 'custom') setRange(presetRange(key)); };

  const rows = data?.rows || [];
  const summary = data?.summary;

  const filteredRows = useMemo(() => {
    let out = rows;
    if (statusFilter === 'owed') out = rows.filter((r) => r.owed > 0);
    else if (statusFilter === 'collected') out = rows.filter((r) => r.paid > 0 && r.paymentStatus !== 'refunded');
    else if (statusFilter === 'upcoming') out = rows.filter((r) => r.isUpcoming);
    else if (statusFilter === 'cancelled') out = rows.filter((r) => r.status === 'cancelled' || r.paymentStatus === 'refunded');
    const { key, dir } = sort;
    return [...out].sort((a, b) => {
      let av = a[key], bv = b[key];
      if (typeof av === 'number' || typeof bv === 'number') { av = av || 0; bv = bv || 0; }
      else { av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase(); }
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, statusFilter, sort]);

  const toggleSort = (key) => setSort((p) => ({ key, dir: p.key === key && p.dir === 'asc' ? 'desc' : 'asc' }));
  const addOnText = (r) => (r.addOns || []).map((a) => `${a.title} (${formatMoney(a.price)})`).join('; ');

  const exportCSV = () => {
    if (!filteredRows.length) { toast.error('No rows to export'); return; }
    const headers = ['Trip Date', 'Start', 'Booking', 'Customer', 'Email', 'Service', 'Asset', 'Status', 'Payment', 'Total', 'Paid', 'Owed', 'Add-ons', 'Prepaid Gratuity', 'Paid At', 'Notes'];
    const esc = (v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`;
    const lines = filteredRows.map((r) => [
      r.tripDate || '', r.startTime || '', r.confirmationCode, r.customerName, r.customerEmail,
      r.serviceName, r.assetName, r.status, r.paymentStatus, r.total, r.paid, r.owed,
      addOnText(r), r.gratuity || 0, r.paidAt ? new Date(r.paidAt).toLocaleString('en-US') : '', (r.notes || '').replace(/\n/g, ' '),
    ].map(esc).join(','));
    const csv = [headers.map(esc).join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `trips-report-${range.startDate}-to-${range.endDate}.csv`;
    a.click();
    toast.success(`Exported ${filteredRows.length} trips`);
  };

  const SortTh = ({ k, children, align = 'left' }) => (
    <th onClick={() => toggleSort(k)} className={`px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-800 select-none whitespace-nowrap text-${align}`} data-testid={`reports-sort-${k}`}>
      <span className="inline-flex items-center gap-1">{children}<ArrowUpDown className="w-3 h-3 opacity-40" /></span>
    </th>
  );

  return (
    <TabPageLayout
      title="Reports"
      subtitle="Build a trips & money report — pick a range, filter, and export."
      actions={<ExportMenu onExport={(f) => (f === 'csv' ? exportCSV() : null)} formats={['csv']} />}
    >
      {/* One filter toolbar — bird's-eye view of all controls */}
      <div className="card-soft p-3" data-testid="reports-controls">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 pl-1 pr-1">
            <CalendarRange className="w-4 h-4" />
          </span>
          <select value={preset} onChange={(e) => onPreset(e.target.value)} className={selectCls} data-testid="reports-preset">
            {PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <DateRangeControl
            startDate={range.startDate}
            endDate={range.endDate}
            onStartChange={(v) => { setPreset('custom'); setRange((p) => ({ ...p, startDate: v })); }}
            onEndChange={(v) => { setPreset('custom'); setRange((p) => ({ ...p, endDate: v })); }}
          />
          <div className="hidden md:block w-px h-6 bg-gray-200 mx-1" />
          <span className="inline-flex items-center text-gray-400 pl-1">
            <SlidersHorizontal className="w-4 h-4" />
          </span>
          <select value={dateBasis} onChange={(e) => setDateBasis(e.target.value)} className={selectCls} data-testid="reports-basis">
            {BASIS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
          </select>
          <select value={serviceId} onChange={(e) => setServiceId(e.target.value)} className={selectCls} data-testid="reports-service-filter">
            <option value="all">All services</option>
            {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={assetId} onChange={(e) => setAssetId(e.target.value)} className={selectCls} data-testid="reports-asset-filter">
            <option value="all">All assets</option>
            {assets.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      </div>

      {/* Minimalist analytics tiles */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-4" data-testid="reports-summary">
          <MetricTile label="Trips" value={summary.tripCount} icon={FileBarChart} hint={`${summary.counts.upcoming} upcoming`} testid="reports-tile-trips" />
          <MetricTile label="Collected" value={formatMoney(summary.collected)} icon={DollarSign} tone="primary" hint={`${summary.counts.paid} paid in full`} testid="reports-tile-collected" />
          <MetricTile label="Owed" value={formatMoney(summary.owed)} icon={AlertCircle} tone="warn" hint={`${summary.counts.owed} trips`} testid="reports-tile-owed" />
          <MetricTile label="Add-ons" value={formatMoney(summary.addOnsTotal)} icon={Gift} hint={`${formatMoney(summary.gratuityTotal)} prepaid gratuity`} testid="reports-tile-addons" />
          <MetricTile label="Refunded" value={formatMoney(summary.refunded)} icon={RotateCcw} hint={`${summary.counts.refunded} trips`} testid="reports-tile-refunded" />
        </div>
      )}

      {/* Blue filter chips */}
      <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="reports-chips">
        {STATUS_CHIPS.map((c) => (
          <button
            key={c.id}
            onClick={() => setStatusFilter(c.id)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${statusFilter === c.id ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
            data-testid={`reports-chip-${c.id}`}
          >
            {c.label}
          </button>
        ))}
        <span className="ml-auto text-sm text-gray-400">{filteredRows.length} trips shown</span>
      </div>

      {/* Table */}
      {loading ? (
        <PageLoader />
      ) : filteredRows.length === 0 ? (
        <div className="mt-4"><EmptyState icon={<FileBarChart />} title="No trips in this view" description="Try a wider date range or a different filter." /></div>
      ) : (
        <div className="card-soft overflow-hidden mt-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="reports-table">
              <thead className="bg-gray-50/60 border-b border-gray-100">
                <tr>
                  <SortTh k="tripDate">Trip Date</SortTh>
                  <SortTh k="confirmationCode">Booking</SortTh>
                  <SortTh k="customerName">Customer</SortTh>
                  <SortTh k="serviceName">Service</SortTh>
                  <SortTh k="assetName">Asset</SortTh>
                  <SortTh k="paymentStatus">Payment</SortTh>
                  <SortTh k="total" align="right">Total</SortTh>
                  <SortTh k="paid" align="right">Paid</SortTh>
                  <SortTh k="owed" align="right">Owed</SortTh>
                  <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left">Add-ons</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredRows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/60" data-testid={`reports-row-${r.confirmationCode}`}>
                    <td className="px-3 py-2.5 whitespace-nowrap text-gray-700">{fmtDate(r.tripDate)}{r.startTime && r.startTime !== '00:00' ? <span className="text-gray-400"> · {r.startTime}</span> : null}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap font-mono text-xs text-gray-500">{r.confirmationCode}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-gray-900 font-medium">{r.customerName}</td>
                    <td className="px-3 py-2.5 text-gray-600 max-w-[180px] truncate" title={r.serviceName}>{r.serviceName}</td>
                    <td className="px-3 py-2.5 text-gray-600 max-w-[140px] truncate" title={r.assetName}>{r.assetName}</td>
                    <td className="px-3 py-2.5"><PaymentBadge status={r.paymentStatus} /></td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap text-gray-700">{formatMoney(r.total)}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap text-blue-600">{formatMoney(r.paid)}</td>
                    <td className={`px-3 py-2.5 text-right whitespace-nowrap font-medium ${r.owed > 0 ? 'text-amber-600' : 'text-gray-300'}`}>{r.owed > 0 ? formatMoney(r.owed) : '—'}</td>
                    <td className="px-3 py-2.5 text-gray-500 max-w-[220px] truncate" title={addOnText(r)}>
                      {r.gratuity > 0 && <span className="inline-flex items-center gap-1 text-blue-500 mr-1"><Gift className="w-3 h-3" />{formatMoney(r.gratuity)}</span>}
                      {(r.addOns || []).filter((a) => a.kind !== 'gratuity').map((a) => a.title).join(', ') || (r.gratuity > 0 ? '' : '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Breakdown by service & asset */}
      {!loading && data && rows.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <DataCard title="By service" subtitle="Collected vs owed in this range">
            <Breakdown rows={data.byService} formatMoney={formatMoney} testid="reports-by-service" />
          </DataCard>
          <DataCard title="By asset" subtitle="Collected vs owed in this range">
            <Breakdown rows={data.byAsset} formatMoney={formatMoney} testid="reports-by-asset" />
          </DataCard>
        </div>
      )}
    </TabPageLayout>
  );
};

const Breakdown = ({ rows, formatMoney, testid }) => (
  <div className="space-y-1.5" data-testid={testid}>
    <div className="flex items-center justify-between gap-3 text-[11px] uppercase tracking-wide text-gray-400 pb-1">
      <span className="flex-1">Name</span><span className="w-12 text-right">Trips</span><span className="w-24 text-right">Collected</span><span className="w-24 text-right">Owed</span>
    </div>
    {(rows || []).slice(0, 8).map((r) => (
      <div key={r.name} className="flex items-center justify-between gap-3 text-sm py-1.5 border-b border-gray-50 last:border-0">
        <span className="text-gray-700 truncate flex-1" title={r.name}>{r.name}</span>
        <span className="text-gray-400 text-xs w-12 text-right">{r.count}×</span>
        <span className="text-blue-600 w-24 text-right">{formatMoney(r.collected)}</span>
        <span className={`w-24 text-right ${r.owed > 0 ? 'text-amber-600' : 'text-gray-300'}`}>{r.owed > 0 ? formatMoney(r.owed) : '—'}</span>
      </div>
    ))}
  </div>
);

export default ReportsTab;
